import React, {useState, useCallback, useEffect} from "react";
import "../App.css";
import { Helmet } from "react-helmet";
import ExcelJS from "exceljs";
import { useDropzone } from "react-dropzone";
import ItemList from "../components/ItemList";
import axios from "axios";
import "../CSS/programmes/OnOffList.css";

import { DefaultButton, SubmitButton } from "../components/Buttons.tsx";
import Navbar from "../components/Navbar.tsx";
import { ColorModeIcon } from "../context/ColorModeContext.tsx";
import { LinearProgressWithLabel } from "../components/Progressbar.tsx";
import SearchableMultiSelect from "../components/SearchableMultiSelect.tsx";
import CustomCheckbox from "../components/CustomCheckbox.tsx";

// Form of the Data that is delivered by the get_markets request to the backend
interface MarketData {
  [key: string]: string; // assuming the values are strings, change it if needed
}

const OnOffList = () => {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

  // States for mandatory information
  const [items, setItems] = useState<{ itemID: number; varID: number }[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [selectedCheckbox, setSelectedCheckbox] = useState<string | null>(null);

  // State for the different online selections:
  const [selectedLieferzeit, setSelectedLieferzeit] = useState("1");
  const [selectedMarkierung2, setSelectedMarkierung2] = useState("8");
  const [selectedLager, setSelectedLager] = useState(
    "Kommissionslager Bestellartikel"
  );

  const [isProgbarVisible, setIsProgbarVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFailed, setIsFailed] = useState<boolean>(false);
  const [progress, setProgress] = useState(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [infoMessage, setinfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Markets and Selected Markets
  const [markets, setMarkets] = useState<{ value: string | number; label: string }[]>([]);
  const [selectedMarkets, setSelectedMarkets] = useState<{ value: string | number; label: string }[]>([]);
  const [overwriteMarkets, setOverwriteMarkets] = useState<boolean>(false);
  // Callback for loading data from the backend when this page opens up

  useEffect(() => {

    const fetchData = async () => {
      try {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        };

        const response = await axios.get<MarketData>(`${backendUrl}/general/get_markets/`, config)
        const formattedData = Object.entries(response.data).map(([label, value]) => (
          {label: label, value: value}));
        setMarkets(formattedData);
      } catch (error) {
        setErrorMessage("Fehler beim laden der Märkte. Bitte lade die Seite erneut oder wende dich an Jonathan.");
      }
    };

    fetchData();

  }, []);

  // ------------------------------------------------ Default Options ------------------------------------------------
  const renderDefaultOptions = () => {
    return (
      <div className="OnOffListPage">
        <div style={{ position: "relative" }}>
          <h3>Stelle mit diesem Programm ein Item On- oder Offline!</h3>
          <div {...getRootProps()} className="drag-drop-field">
            <input {...getInputProps()} />
            {!uploadedFile && (
              <p>
                Ziehe eine Excel-Datei in dieses Feld oder klicke hinein und
                wähle sie aus!
              </p>
            )}
            {uploadedFile && (
              <div>
                <p>Hochgeladene Datei: {uploadedFile.name}</p>
                <DefaultButton
                  label="Auswahl entfernen"
                  onClick={deleteFile}
                  size="small"
                  isDisabled={isLoading}
                />
              </div>
            )}
          </div>
          <div>
            {/*Render ItemList component to display the items*/}
            {items.length > 0 && <ItemList items={items} />}
          </div>
        </div>
        <div className="item-on-off-checkbox-container">
          <label>
            <input
              type="checkbox"
              name="checkboxOnline"
              checked={selectedCheckbox === "checkboxOnline"}
              onChange={handleCheckboxChange}
              hx-get="/api/toggleCheckbox1"
              hx-trigger="change"
              // onClick={handleOnlineBoxClick}
              className="itemOnOffCheckbox"
            />
            Online
          </label>
          <label>
            <input
              type="checkbox"
              name="checkboxOffline"
              checked={selectedCheckbox === "checkboxOffline"}
              onChange={handleCheckboxChange}
              hx-get="/api/toggleCheckbox2"
              hx-trigger="change"
              className="itemOnOffCheckbox"
            />
            Offline
          </label>
        </div>
      </div>
    );
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setSelectedCheckbox(name);
    console.log(name);
    setErrorMessage(null);
  };

  // Handler for file input
  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: any[]) => {
      if (fileRejections.length > 0) {
        const file = fileRejections[0].file;
        const s =
          "Bitte lade eine lesbare Excel-Datei hoch! " +
          file.name +
          " ist nicht lesbar.";
        alert(s);
        setErrorMessage(s);
        deleteFile(); // deletion of current accepted file
      } else {
        setErrorMessage(null);
        const file = acceptedFiles[0];
        if (file) {
          setUploadedFile(file);
          processFile(file);
          setIsProgbarVisible(false);
          setinfoMessage(null);
        }
      }
    },
    []
  );

  // Function to handle file deletion
  const deleteFile = () => {
    setErrorMessage(null);
    setUploadedFile(null);
    setItems([]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "application/vnd.ms-excel": [],
    },
  });

  const processFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const buffer = evt.target?.result;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer as ArrayBuffer);

      // Check if workbook contains any worksheets
      if (workbook.worksheets.length === 0) {
        const err = "Die Excel-Dabei enthält keine worksheets!";
        setErrorMessage(err);
        alert(err);
        throw new Error(err);
      }

      const worksheet = workbook.worksheets[0];
      const itemsData: { itemID: number; varID: number }[] = [];

      let error_rows: string = ""; // For displaying erroneous rows in the excel file

      const header = worksheet.getRow(1);
      let item_id_col = null;
      let var_id_col = null;
      // Helper to clean up value: cast to string -> lowercase -> remove dots
      const normalize = (val: any) => String(val || "").toLowerCase().replace(/[^a-z0-9]/g, "");

      header.eachCell((cell, colNumber) => {
        const val = normalize(cell.value);
        if (val === "itemid") {
          item_id_col = colNumber;
        } else if (val === "variationid" || val === "varid") {
          var_id_col = colNumber;
        }
      });
      if (item_id_col === null && var_id_col === null){
        const err_text = "Spalte \"Item.id\" und Spalte \"Variation.id\" konnten nicht ermittelt werden.";
        setErrorMessage(err_text);
        alert(err_text);
      } else if (item_id_col === null){
        const err_text = "Spalte \"Item.id\" konnte nicht ermittelt werden.";
        setErrorMessage(err_text);
        alert(err_text);
      } else if (var_id_col === null){
        const err_text = "Spalte \"Variation.id\" konnte nicht ermittelt werden.";
        setErrorMessage(err_text);
        alert(err_text);
      }

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          // Skip header row
          const itemID = row.getCell(item_id_col).value as number;
          const varID = row.getCell(var_id_col).value as number;
          if (
            varID == null ||
            isNaN(varID) ||
            itemID == null ||
            isNaN(itemID)
          ) {
            error_rows =
              error_rows +
              "Reihe " +
              rowNumber +
              "  -  itemID: " +
              itemID +
              ", varID: " +
              varID +
              "\n";
          } else {
            itemsData.push({ itemID, varID });
          }
        }
      });
      if (error_rows != "") {
        error_rows =
          "Folgende Reihen enthalten eine nicht-lesbare Item-ID oder Varianten-ID und werden ignoriert! \n" +
          error_rows;
        setErrorMessage(error_rows);
        alert(error_rows);
      }
      setItems(itemsData);
    };
    reader.readAsArrayBuffer(file);
  };

  // ------------------------------------------------ Online Selection ------------------------------------------------
  const handleOnlineSelectionChange =
    (form: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (form === "Lieferzeit") {
        setSelectedLieferzeit(e.currentTarget.value);
      } else if (form === "Markierung_2") {
        setSelectedMarkierung2(e.currentTarget.value);
      } else if (form === "Lager") {
        setSelectedLager(e.currentTarget.value);
      }
    };

  const renderOnlineOptions = () => {
    type Visibility = "visible" | "hidden";
    let vis: Visibility;

    if (selectedCheckbox === "checkboxOnline") {
      vis = "visible";
    } else {
      vis = "hidden";
    }

    return (
      <div style={{visibility: vis}}>
        <div className="wrapper">
          <div className="container">
            <label htmlFor="Lieferzeit">Lieferzeit:</label>
            <select
              name="Lieferzeit"
              id="Lieferzeit"
              className="itemOnOffDropdown"
              onChange={handleOnlineSelectionChange("Lieferzeit")}
            >
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10</option>
            </select>
            <br/>
            <img
              src="/availPic.png"
              alt="Verschiedene Lieferzeit Optionen"
              style={{width: "80%"}}
            />
          </div>

          <div className="container">
            <label htmlFor="Markierung_2">Markierung 2:</label>
            <select
              name="Markierung_2"
              id="Markierung_2"
              className="itemOnOffDropdown"
              onChange={handleOnlineSelectionChange("Markierung_2")}
            >
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10</option>
              <option value="11">11</option>
            </select>
            <br/>
            <img src="/flag2Pic.png" alt="Legende für Markierung 2"/>
          </div>

          <div className="container">
            <label htmlFor="Lager">Lager:</label>
            <select
              name="Lager"
              id="Lager"
              className="itemOnOffDropdown"
              value={selectedLager}
              onChange={handleOnlineSelectionChange("Lager")}
            >
              <option value="Kommissionslager Bestellartikel">
                Kommissionslager
              </option>
              <option value="Dropshipping">Dropshipping</option>
            </select>
          </div>
        </div>
        <div className="container">
          <SearchableMultiSelect items={markets} inputLabel="Märkte" selectAllOption={true} selectAllInitially={true}
                                 value={selectedMarkets} setValue={setSelectedMarkets}/>
        </div>
        <div className="container">
          <CustomCheckbox
            value={overwriteMarkets}
            label="Marktverfügbarkeiten überschreiben"
            onChange={checked => setOverwriteMarkets(checked)}
            is_disabled={false}
          />
        </div>
      </div>
    );
  };

  // ------------------------------------------------ GO BUTTON ------------------------------------------------

  const handleGoButton = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsFailed(false);
    setProgress(null);
    setProgressMessage(null);

    if (items.length === 0) {
      setinfoMessage(null);
      const err = "Bitte lade eine Excel-Datei hoch";
      setErrorMessage(err);
      alert(err);
      return;
    }
    const items_json = items.map((item) => ({
      item_id: item.itemID,
      var_id: item.varID,
    }));
    setinfoMessage(null);
    setErrorMessage(null);

    let url : string;
    if (selectedCheckbox === "checkboxOnline") {url = `${backendUrl}/OnlineOffline_Liste/online/`}
    else if (selectedCheckbox === "checkboxOffline") {url = `${backendUrl}/OnlineOffline_Liste/offline/`}
    else {
      const err =
          "Bitte wähle aus, ob das Item online oder offline gestellt werden soll";
      setErrorMessage(err);
      alert(err);
      return;
    }
    const marketIds = selectedMarkets.map((market) => market.value);
    const data = {
        items: items_json,
        availability: selectedLieferzeit,
        flag_2: selectedMarkierung2,
        lager: selectedLager,
        market_ids: marketIds,
        overwrite_markets: overwriteMarkets
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    setIsProgbarVisible(true);
    setIsLoading(true);

    try {
      const res = await axios.put(url, data, config);
      const taskId = res.data.task_id;
      console.log("Task ID: " + taskId);
      await pollTaskStatus(taskId, config);
    } catch (err) {
        handleError(err);
        setIsLoading(false);
    }
  }

  const pollTaskStatus = async (taskId:number, config) => {
        const intervalId = setInterval(async () => {
          try {
            const statusRes = await axios.get(
              `${backendUrl}/OnlineOffline_Liste/task-status/${taskId}/`,
              config
            );

            const { state, progress, message } = statusRes.data;

            if (state === "PENDING" || state === "PROGRESS") {
              setProgressMessage(message);
              setProgress(progress);
            } else if (state === "SUCCESS") {
              clearInterval(intervalId);

              const failure = message?.FAILURE;
              if (failure == null){ // IF the backend did not manually return an error
                const successful = message.successful;
                const unsuccessful = message.unsuccessful;

                if (Object.keys(unsuccessful).length > 0){
                  setErrorMessage(
                  "Bei folgenden Item IDs traten Fehler auf: \n" +
                    Object.entries(unsuccessful)
                      .map(([item_id, error]) => `${item_id}: ${error}`)
                      .join("\n")
                  );
                }
                if (successful.length > 0) {
                  setinfoMessage(
                    "Folgende Items wurden erfolgreich hochgeladen: \n" +
                      successful.join(", ")
                  );
                }
                setProgress(100);
                setProgressMessage(null);
                setIsLoading(false);
              } else { // IF the backend returned manually with an error
                alert(failure);
                clearInterval(intervalId);
                setinfoMessage(null);
                setErrorMessage(failure);
                setIsFailed(true);
                setProgress(100);
                setProgressMessage(null);
                setIsLoading(false);
              }
            }
          } catch (err) {
            alert(err);
            alert(err.status);
            handleError(err);
            clearInterval(intervalId);
          }
       }, 3500); // Poll every 3.5 seconds
      };
    const handleError = (err) => {
          let error_str = null;
          if (!err.response) {
            error_str = "Netzwerkfehler. Deine Internetverbindung ist unterbrochen.";
          } else if (err.response.status === 500) {
            error_str = "Interner Serverfehler. Screenshot an die Entwickler senden.";
          } else {
            error_str = err.response.data.detail.toString();
          }
          setinfoMessage(null);
          setErrorMessage(error_str);
          setIsLoading(false);
          setIsFailed(true);
          // alert(error_str);
        };

  // ------------------------------------------------ Error Message ------------------------------------------------
  const renderErrorMessage = () => {
    if (errorMessage) {
      return (
        <div className="error-message-container">
          {" "}
          <p>{errorMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };

  const renderInfoMessage = () => {
    if (infoMessage) {
      return (
        <div className="info-message-container">
          {" "}
          <p>{infoMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <>
      <Navbar />
      <Helmet>
        <title>{"Online Offline (Liste)"}</title>
      </Helmet>
      {renderDefaultOptions()}
      {renderOnlineOptions()}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "25px",
          alignItems: "center",
        }}
      >
        <SubmitButton
          onClick={handleGoButton}
          label={
            "Items " +
            (selectedCheckbox === "checkboxOnline" ? "online" : "offline") +
            " stellen"
          }
          size="small"
          type="submit"
          manualLoading={isLoading}
        />
      </div>
      {isProgbarVisible &&
          <LinearProgressWithLabel progress={progress} progressMessage={progressMessage} isFailed={isFailed} />}
      {renderErrorMessage()}
      {renderInfoMessage()}
      {ColorModeIcon()}
    </>
  );
};

export default OnOffList;
